import { type RefObject, useEffect, useState } from "react";

type OnScreenOptions = {
  rootMargin?: string;
  /** Stop observing; the hook then reports false until it is enabled again. */
  enabled?: boolean;
};

/** Whether the element is on screen right now — it flips back to false once
 *  the element leaves, unlike the latching [useNearViewport]. */
export const useOnScreen = (
  ref: RefObject<Element | null>,
  { rootMargin = "0px", enabled = true }: OnScreenOptions = {},
): boolean => {
  const [onScreen, setOnScreen] = useState(false);
  const [element, setElement] = useState<Element | null>(null);

  // The observed element can mount later than this hook runs (a poster that
  // swaps to a video once its source is known), so re-read the ref each render.
  useEffect(() => {
    setElement(enabled ? ref.current : null);
  });

  useEffect(() => {
    if (element === null) {
      setOnScreen(false);
      return;
    }

    if (typeof IntersectionObserver === "undefined") {
      setOnScreen(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => setOnScreen(entry?.isIntersecting === true),
      { rootMargin },
    );
    observer.observe(element);

    return () => observer.disconnect();
  }, [element, rootMargin]);

  return onScreen;
};
